/**
 * StrategyCanvasActions — Actions dropdown menu for strategy canvas.
 *
 * Used in both blocks mode (BlockCanvasEditor) and screens mode
 * (OnboardingCanvasEditorV2 in strategy mode). Follows the same pattern
 * as the onboarding canvas action bar.
 *
 * U9 (Spec D4, AC-D4-6): the menu speaks INTENT, not transform verbs. No
 * association → only "Create Onboarding"; associated → "Send to Onboarding" /
 * "Get from Onboarding" against the canonical link (Create and Send are never
 * co-visible). Rare paths (raw compile, export/import, theme tools) live in
 * an "Advanced…" submenu. Existing callbacks and data-testids are unchanged —
 * only labels and grouping moved.
 */
import React, { useState, useCallback } from 'react';
import { Button, ListSubheader, Menu, MenuItem, Divider } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import FileUploadIcon from '@mui/icons-material/FileUpload';
import PaletteIcon from '@mui/icons-material/Palette';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import CloudDownloadIcon from '@mui/icons-material/CloudDownload';
import AssessmentIcon from '@mui/icons-material/Assessment';
import LockIcon from '@mui/icons-material/Lock';
import EditIcon from '@mui/icons-material/Edit';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import { ListItemText, Tooltip } from '@mui/material';
import type { StrategyStatus } from '@segmently/schema-models/strategy';

interface StrategyCanvasActionsProps {
  mode: 'blocks' | 'screens';
  onExport: () => void;
  onImport: () => void;
  onEditTheme: () => void;
  onReplaceTheme?: () => void;
  onUpdateUiFromTheme?: () => void;
  onCompileToOnboarding?: () => void;
  onLiftFromOnboarding?: () => void;
  onPullFromOnboarding?: () => void;
  onPushToOnboarding?: () => void;
  onScoreStrategy?: () => void;
  /** Lifecycle (Spec A / D51) — strategy-level, blocks mode only. */
  /** UXR C4: launch generation for the SAVED strategy (blocks mode only). */
  onGenerate?: () => void;
  generateDisabledReason?: string;
  onBake?: () => void;
  onStartNewVersion?: () => void;
  /** Spec C (C4): insight-impact report (Mode 3 dry-run) — blocks mode only. */
  onInsightImpact?: () => void;
  /** U9 (Spec D4): canonical association — flips Create → Send/Get intent grouping. */
  associatedFunnelId?: string | null;
  /** Display name of the associated onboarding for the menu caption. */
  associatedFunnelName?: string | null;
  strategyStatus?: StrategyStatus;
  exportDisabled?: boolean;
  compileDisabled?: boolean;
  liftDisabled?: boolean;
  pullDisabled?: boolean;
  pushDisabled?: boolean;
  themeDisabled?: boolean;
  replaceThemeDisabled?: boolean;
  updateUiFromThemeDisabled?: boolean;
  scoreDisabled?: boolean;
  lifecycleDisabled?: boolean;
}

export const StrategyCanvasActions: React.FC<StrategyCanvasActionsProps> = ({
  mode,
  onGenerate,
  generateDisabledReason,
  onExport,
  onImport,
  onEditTheme,
  onReplaceTheme,
  onUpdateUiFromTheme,
  onCompileToOnboarding,
  onLiftFromOnboarding,
  onPullFromOnboarding,
  onPushToOnboarding,
  onScoreStrategy,
  onBake,
  onStartNewVersion,
  onInsightImpact,
  associatedFunnelId,
  associatedFunnelName,
  strategyStatus,
  exportDisabled = false,
  compileDisabled = false,
  liftDisabled = false,
  pullDisabled = false,
  pushDisabled = false,
  themeDisabled = false,
  replaceThemeDisabled = false,
  updateUiFromThemeDisabled = false,
  scoreDisabled = false,
  lifecycleDisabled = false,
}) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const open = Boolean(anchorEl);
  // U9: "Advanced…" nested submenu (rare paths only).
  const [advancedAnchor, setAdvancedAnchor] = useState<HTMLElement | null>(null);

  const handleOpen = useCallback((e: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(e.currentTarget);
  }, []);

  const handleClose = useCallback(() => {
    setAdvancedAnchor(null);
    setAnchorEl(null);
  }, []);

  const handleOpenAdvanced = useCallback((e: React.MouseEvent<HTMLElement>) => {
    setAdvancedAnchor(e.currentTarget);
  }, []);

  const handleExport = useCallback(() => {
    handleClose();
    onExport();
  }, [onExport, handleClose]);

  const handleImport = useCallback(() => {
    handleClose();
    onImport();
  }, [onImport, handleClose]);

  const handleEditTheme = useCallback(() => {
    handleClose();
    onEditTheme();
  }, [onEditTheme, handleClose]);

  const handleReplaceTheme = useCallback(() => {
    handleClose();
    onReplaceTheme?.();
  }, [onReplaceTheme, handleClose]);

  const handleUpdateUiFromTheme = useCallback(() => {
    handleClose();
    onUpdateUiFromTheme?.();
  }, [onUpdateUiFromTheme, handleClose]);

  const handleCompileToOnboarding = useCallback(() => {
    handleClose();
    onCompileToOnboarding?.();
  }, [onCompileToOnboarding, handleClose]);

  const handleLiftFromOnboarding = useCallback(() => {
    handleClose();
    onLiftFromOnboarding?.();
  }, [onLiftFromOnboarding, handleClose]);

  const handlePullFromOnboarding = useCallback(() => {
    handleClose();
    onPullFromOnboarding?.();
  }, [onPullFromOnboarding, handleClose]);

  const handlePushToOnboarding = useCallback(() => {
    handleClose();
    onPushToOnboarding?.();
  }, [onPushToOnboarding, handleClose]);

  const handleScoreStrategy = useCallback(() => {
    handleClose();
    onScoreStrategy?.();
  }, [onScoreStrategy, handleClose]);

  const handleBake = useCallback(() => {
    handleClose();
    onBake?.();
  }, [onBake, handleClose]);

  const handleStartNewVersion = useCallback(() => {
    handleClose();
    onStartNewVersion?.();
  }, [onStartNewVersion, handleClose]);

  const handleInsightImpact = useCallback(() => {
    handleClose();
    onInsightImpact?.();
  }, [onInsightImpact, handleClose]);

  // Lifecycle (Spec A / D51): Bake mints a version from the draft head; Start New
  // Version reopens a baked/published head for editing. A legacy strategy with no
  // status yet reads as an editable draft.
  const isDraft = !strategyStatus || strategyStatus === 'draft';

  const exportLabel = mode === 'blocks' ? 'Export Strategy' : 'Export Block';
  const importLabel = mode === 'blocks' ? 'Import Strategy' : 'Import Block';

  // U9: intent grouping — the canonical association decides Create vs Send/Get.
  const hasAssociation = Boolean(associatedFunnelId);
  const associationCaption = hasAssociation
    ? `Onboarding${associatedFunnelName ? ` — “${associatedFunnelName}”` : ''}`
    : 'Onboarding';

  return (
    <>
      <Button
        variant="outlined"
        size="small"
        endIcon={<ExpandMoreIcon />}
        onClick={handleOpen}
        // UXR C10 (D18): blocks + screens editors can be mounted simultaneously
        // (screens modal over the strategy canvas) — the testid must be unique.
        data-testid={mode === 'blocks' ? 'strategy-canvas-actions-btn' : 'strategy-canvas-actions-btn-screens'}
        sx={{
          minWidth: 'auto',
          px: 1.5,
          py: 0.5,
          fontSize: '0.8125rem',
          textTransform: 'none',
        }}
      >
        Actions
      </Button>

      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        data-testid="strategy-canvas-actions-menu"
      >
        {/* ── U9 intent group: the association decides the vocabulary ── */}
        {(onCompileToOnboarding || onLiftFromOnboarding || onPullFromOnboarding || onPushToOnboarding) && (
          <ListSubheader disableSticky sx={{ lineHeight: '28px' }}>{associationCaption}</ListSubheader>
        )}
        {mode === 'blocks' && !hasAssociation && onCompileToOnboarding && (
          <MenuItem
            onClick={handleCompileToOnboarding}
            disabled={compileDisabled}
            data-testid="strategy-canvas-compile-onboarding"
          >
            <AccountTreeIcon sx={{ mr: 1, fontSize: 20 }} />
            Create Onboarding
          </MenuItem>
        )}
        {hasAssociation && onPushToOnboarding && (
          <MenuItem
            onClick={handlePushToOnboarding}
            disabled={pushDisabled}
            data-testid="strategy-canvas-push-onboarding"
          >
            <CloudUploadIcon sx={{ mr: 1, fontSize: 20 }} />
            Send to Onboarding
          </MenuItem>
        )}
        {mode === 'blocks' && hasAssociation && onLiftFromOnboarding && (
          <MenuItem
            onClick={handleLiftFromOnboarding}
            disabled={liftDisabled}
            data-testid="strategy-canvas-lift-onboarding"
          >
            <CloudDownloadIcon sx={{ mr: 1, fontSize: 20 }} />
            Get from Onboarding
          </MenuItem>
        )}
        {mode === 'screens' && onPullFromOnboarding && (
          <MenuItem
            onClick={handlePullFromOnboarding}
            disabled={pullDisabled}
            data-testid="strategy-canvas-pull-onboarding"
          >
            <CloudDownloadIcon sx={{ mr: 1, fontSize: 20 }} />
            Get Block from Onboarding
          </MenuItem>
        )}
        {mode === 'screens' && !hasAssociation && onPushToOnboarding && (
          <MenuItem
            onClick={handlePushToOnboarding}
            disabled={pushDisabled}
            data-testid="strategy-canvas-push-onboarding"
          >
            <CloudUploadIcon sx={{ mr: 1, fontSize: 20 }} />
            Send Block to Onboarding
          </MenuItem>
        )}
        {(onCompileToOnboarding || onLiftFromOnboarding || onPullFromOnboarding || onPushToOnboarding) && <Divider />}

        {/* ── Generation (UXR C4) ── */}
        {mode === 'blocks' && onGenerate && (
          <ListSubheader disableSticky sx={{ lineHeight: '28px' }}>Generation</ListSubheader>
        )}
        {mode === 'blocks' && onGenerate && (
          <Tooltip title={generateDisabledReason ?? ''} placement="left">
            <span>
              <MenuItem
                onClick={() => { handleClose(); onGenerate(); }}
                disabled={Boolean(generateDisabledReason)}
                data-testid="strategy-generate-action"
              >
                <AutoAwesomeIcon sx={{ mr: 1, fontSize: 20 }} />
                <ListItemText
                  primary="Generate blocks with AI…"
                  secondary={generateDisabledReason}
                  secondaryTypographyProps={{ variant: 'caption' }}
                />
              </MenuItem>
            </span>
          </Tooltip>
        )}
        {mode === 'blocks' && onGenerate && <Divider />}

        {/* ── Version lifecycle (Spec A / D51) ── */}
        {mode === 'blocks' && (onBake || onStartNewVersion || onInsightImpact) && (
          <ListSubheader disableSticky sx={{ lineHeight: '28px' }}>Version</ListSubheader>
        )}
        {mode === 'blocks' && onBake && (
          <MenuItem
            onClick={handleBake}
            disabled={lifecycleDisabled || !isDraft}
            data-testid="strategy-canvas-bake"
          >
            <LockIcon sx={{ mr: 1, fontSize: 20 }} />
            {isDraft ? 'Bake Version' : 'Baked (read-only)'}
          </MenuItem>
        )}
        {mode === 'blocks' && onStartNewVersion && (
          <MenuItem
            onClick={handleStartNewVersion}
            disabled={lifecycleDisabled || isDraft}
            data-testid="strategy-canvas-start-new-version"
          >
            <EditIcon sx={{ mr: 1, fontSize: 20 }} />
            Start New Version
          </MenuItem>
        )}
        {mode === 'blocks' && onInsightImpact && (
          <MenuItem onClick={handleInsightImpact} data-testid="strategy-canvas-insight-impact">
            <TrendingUpIcon sx={{ mr: 1, fontSize: 20 }} />
            Insight Impact…
          </MenuItem>
        )}
        {onScoreStrategy && (
          <MenuItem
            onClick={handleScoreStrategy}
            disabled={scoreDisabled}
            data-testid="strategy-canvas-score-strategy"
          >
            <AssessmentIcon sx={{ mr: 1, fontSize: 20 }} />
            Score Strategy
          </MenuItem>
        )}
        <Divider />

        {/* ── Rare paths live behind Advanced… (U9) ── */}
        <MenuItem onClick={handleOpenAdvanced} data-testid="strategy-canvas-advanced">
          Advanced…
          <ChevronRightIcon sx={{ ml: 'auto', fontSize: 20 }} />
        </MenuItem>
      </Menu>

      <Menu
        anchorEl={advancedAnchor}
        open={Boolean(advancedAnchor)}
        onClose={() => setAdvancedAnchor(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        data-testid="strategy-canvas-advanced-menu"
      >
        {mode === 'blocks' && hasAssociation && onCompileToOnboarding && (
          <MenuItem
            onClick={handleCompileToOnboarding}
            disabled={compileDisabled}
            data-testid="strategy-canvas-compile-onboarding"
          >
            <AccountTreeIcon sx={{ mr: 1, fontSize: 20 }} />
            Create a new onboarding (Compile)
          </MenuItem>
        )}
        {mode === 'blocks' && !hasAssociation && onLiftFromOnboarding && (
          <MenuItem
            onClick={handleLiftFromOnboarding}
            disabled={liftDisabled}
            data-testid="strategy-canvas-lift-onboarding"
          >
            <CloudDownloadIcon sx={{ mr: 1, fontSize: 20 }} />
            Get from another onboarding…
          </MenuItem>
        )}
        <MenuItem onClick={handleExport} disabled={exportDisabled} data-testid="strategy-canvas-export">
          <FileDownloadIcon sx={{ mr: 1, fontSize: 20 }} />
          {exportLabel}
        </MenuItem>
        <MenuItem onClick={handleImport} data-testid="strategy-canvas-import">
          <FileUploadIcon sx={{ mr: 1, fontSize: 20 }} />
          {importLabel}
        </MenuItem>
        <Divider />
        <MenuItem onClick={handleEditTheme} disabled={themeDisabled} data-testid="strategy-canvas-edit-theme">
          <PaletteIcon sx={{ mr: 1, fontSize: 20 }} />
          Edit Theme
        </MenuItem>
        {onReplaceTheme && (
          <MenuItem
            onClick={handleReplaceTheme}
            disabled={replaceThemeDisabled}
            data-testid="strategy-canvas-replace-theme"
          >
            <PaletteIcon sx={{ mr: 1, fontSize: 20 }} />
            Replace Theme...
          </MenuItem>
        )}
        {onUpdateUiFromTheme && (
          <MenuItem
            onClick={handleUpdateUiFromTheme}
            disabled={updateUiFromThemeDisabled}
            data-testid="strategy-canvas-update-ui-from-theme"
          >
            <PaletteIcon sx={{ mr: 1, fontSize: 20 }} />
            Update UI from Current Theme...
          </MenuItem>
        )}
      </Menu>
    </>
  );
};
